import { Empty, Space, Spin, Timeline, Typography } from '@arco-design/web-react';
import { IconRight } from '@arco-design/web-react/icon';
import { useNavigate } from 'react-router-dom';
import PillTag from '../PillTag';
import { usePerm } from '../../hooks/usePerm';
import { aiLogStageLabel } from '../../utils/labels';
import { useIntentNames } from '../../utils/intentName';
import { fmtLatency, fmtMsDetail, fmtTime } from '../../utils/format';
import type { AICallLog } from '../../api/cdr';
import { aiStageColor, aiStagePill, computeLatency, computeSpeakSpans, intentDisplay, isIntentCode, isIntentLog, logSpanText } from './_aiCallLog';
import AIPipeline from './_AiPipeline';

/**
 * AI 调用详细日志时间线：绝对时刻 + 相对通话开始 + 距上一条步长 + 阶段标签 + 语音起止
 * （客户说话 / 机器人播报）+ LLM/TTS 延迟 + 内容/明细。
 *
 * 意图识别行（见 isIntentLog）整行高亮且可点击：跳「智能语音机器人 → 意图识别」词表并按意图码定位，
 * 省去「记住 code → 手动去词表搜」；跳转需 bot.intents（与 App.tsx 该路由守卫同口径），
 * 无权限时该行保持只读，避免点进 403。
 */
export function AILogView({ logs, loading }: { logs: AICallLog[]; loading?: boolean }) {
  const navigate = useNavigate();
  const canViewIntent = usePerm('bot.intents');
  // 意图词表中文名映射：既供 intentDisplay 显示，也供 isIntentCode 判定「content 是否意图码」（可否跳转）
  useIntentNames();
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <Spin />
      </div>
    );
  }
  if (!logs.length) {
    return <Empty description="无 AI 调用日志（人工通话或无日志）" />;
  }
  const latency = computeLatency(logs);
  const speakSpans = computeSpeakSpans(logs);
  return (
    <div>
      <AIPipeline logs={logs} />
      <Timeline style={{ marginTop: 20 }}>
      {logs.map((l, i) => {
        const label = aiLogStageLabel(l.stage);
        const offset = Number(l.offset_ms) || 0;
        const prevOffset = i > 0 ? Number(logs[i - 1].offset_ms) || 0 : 0;
        const delta = i > 0 ? Math.max(0, offset - prevOffset) : 0;
        const lat = latency[l.id];
        // 客户说话起止（asr 行自带）；机器人播报起止（tts_turn/tts_segment 行自带，旧口径 first_frame/end 配对后挂在 tts_end 行）
        const said = l.stage === 'asr_sentence_end' ? logSpanText(l.begin_ms, l.end_ms) : '';
        const speak = speakSpans[l.id];
        const spoke = speak ? logSpanText(speak.beginMs, speak.endMs) : '';
        // 意图码：仅意图行 + content 命中词表（确为意图码）+ 有权限时才跳转（无 code 的空行/动作名行保持只读）
        const intentCode =
          isIntentLog(l.stage) && canViewIntent && isIntentCode(l.content) ? (l.content || '').trim() : '';
        const openIntent = intentCode
          ? () => navigate(`/bot/intents?code=${encodeURIComponent(intentCode)}`)
          : undefined;
        return (
          <Timeline.Item key={l.id} dotColor={aiStageColor(l.stage)}>
            {/* 时间行原先走 Timeline.Item 的 label：Arco 把它渲染在内容之后（labelPosition=same），
                故此处仍放在内容末尾并补回原来的 4px 间距；整行同属一个高亮块，点击/键盘都在块上 */}
            <div
              className={openIntent ? 'bcc-log-intent-row' : undefined}
              role={openIntent ? 'button' : undefined}
              tabIndex={openIntent ? 0 : undefined}
              title={openIntent ? `查看意图「${intentDisplay(l)}」的词表与动作配置` : undefined}
              onClick={openIntent}
              onKeyDown={
                openIntent
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openIntent();
                      }
                    }
                  : undefined
              }
            >
              <div className={openIntent ? 'bcc-log-intent-head' : undefined}>
                <Space size={6} wrap>
                  <PillTag size="sm" color={aiStagePill(l.stage)}>
                    {label}
                  </PillTag>
                  {said && (
                    <PillTag size="sm" color="gray" title="客户说话的起止时间（相对接通时刻）">
                      说话 {said}
                    </PillTag>
                  )}
                  {spoke && (
                    <PillTag size="sm" color="gray" title="机器人播报的起止时间（相对接通时刻）">
                      播报 {spoke}
                    </PillTag>
                  )}
                  {lat?.map((x) => (
                    <PillTag key={x.label} size="sm" color="orange">
                      {x.label} {fmtLatency(x.ms)}
                    </PillTag>
                  ))}
                </Space>
                {openIntent && (
                  <span className="bcc-log-intent-go">
                    查看意图配置
                    <IconRight />
                  </span>
                )}
              </div>
              {l.content && (
                <div
                  className={openIntent ? 'bcc-log-intent-name' : undefined}
                  style={{ fontSize: 13, marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {intentDisplay(l)}
                </div>
              )}
              {l.detail && (
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                  {l.detail}
                </Typography.Text>
              )}
              <div
                title="绝对时刻 · 相对通话开始 · 距上一条的间隔"
                style={{
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  fontSize: 11,
                  lineHeight: '18px',
                  color: 'var(--color-text-4)',
                  marginTop: 4,
                }}
              >
                <span>{fmtTime(l.event_time, 'HH:mm:ss.SSS')}</span>
                <span className="bcc-log-time-sep">·</span>
                <span>相对 +{fmtMsDetail(offset)}</span>
                <span className="bcc-log-time-sep">·</span>
                {i > 0 ? <span>距上条 {fmtLatency(delta)}</span> : <span>通话开始</span>}
              </div>
            </div>
          </Timeline.Item>
        );
      })}
    </Timeline>
    </div>
  );
}
